/**
 * Amended Log popup – lists amendment log entries for a result detail.
 * GET /histopathology/result-page/amend-log/?result_detail_id=<id>
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { ImagePaths } from "../../Utility/ImagePaths";

const closeImg = ImagePaths?.LabCloseButton?.default ?? ImagePaths?.LabCloseButton;
const viewDocImg = ImagePaths?.LabViewButton?.default ?? ImagePaths?.LabViewButton;

function formatAmendedDateTime(isoString) {
  if (!isoString) return "-";
  const dt = DateTime.fromISO(isoString, { zone: "utc" });
  if (!dt.isValid) return isoString;
  return dt.toFormat("dd-MM-yyyy | hh:mm a");
}

function getPreviousReportContent(changes) {
  if (!Array.isArray(changes)) return null;
  const reportChange = changes.find((c) => c?.field === "report_content");
  return reportChange?.old ?? null;
}

export default function AmendedLogPopup({ open, onClose, resultDetailId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previousResultAnchor, setPreviousResultAnchor] = useState({ open: false, html: null });

  useEffect(() => {
    if (!open || !resultDetailId) {
      setList([]);
      return;
    }
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_AMEND_LOG}?result_detail_id=${resultDetailId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success" && Array.isArray(res?.data?.data)) {
          setList(res.data.data);
        } else {
          setList([]);
        }
      })
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [open, resultDetailId]);

  const handleClose = () => {
    setPreviousResultAnchor({ open: false, html: null });
    onClose?.();
  };

  const handleViewPrevious = (html) => {
    setPreviousResultAnchor({ open: true, html });
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 320 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="h6" fontWeight={600}>
              Amended Log
            </Typography>
            <IconButton size="small" onClick={handleClose} aria-label="Close">
              <Box
                component="img"
                src={typeof closeImg === "object" && closeImg?.default ? closeImg.default : closeImg}
                alt="Close"
                sx={{ height: 20, width: 20 }}
              />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 2 }}>
            {loading ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>
                Loading...
              </Typography>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 600 } }}>
                    <TableCell>Amended date &amp; Time</TableCell>
                    <TableCell>Amended by</TableCell>
                    <TableCell>Previous Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        No amended log entries
                      </TableCell>
                    </TableRow>
                  ) : (
                    list.map((row) => {
                      const previousHtml = getPreviousReportContent(row.changes);
                      return (
                        <TableRow key={row.id || row.edited_at}>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            {formatAmendedDateTime(row.edited_at)}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            {row.edited_by ?? "-"}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewPrevious(previousHtml)}
                              disabled={!previousHtml}
                              title="View previous result"
                              sx={{ p: 0.5 }}
                            >
                              <Box
                                component="img"
                                src={typeof viewDocImg === "object" && viewDocImg?.default ? viewDocImg.default : viewDocImg}
                                alt="View"
                                sx={{ height: 22, width: 22 }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog open={previousResultAnchor.open} onClose={() => setPreviousResultAnchor({ open: false, html: null })} maxWidth="md" fullWidth>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Previous Result
          </Typography>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 2,
              maxHeight: 400,
              overflow: "auto",
              backgroundColor: "#fafafa",
            }}
            dangerouslySetInnerHTML={{ __html: previousResultAnchor.html || "<p>-</p>" }}
          />
        </Box>
      </Dialog>
    </>
  );
}
